// src/components/layout/Sidebar.tsx
import React, { useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  HeadphonesIcon,
  Mail,
  Home,
  LogOut,
  Settings,
  User,
  Phone
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePageContext } from '@/contexts/PageContext';
import { usePermissionCheck } from '@/hooks/useGlobalPermission';
import { NavItem } from '@/types';
import { Tooltip } from '@/components/common/Tooltip';

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const { setCurrentPage, collapsed, setCollapsed } = usePageContext();
  const { doLogout, user } = useAuth();
  const { checkPermission } = usePermissionCheck();

  // Use a mapping for refs based on unique paths
  const itemRefs = useRef<Record<string, HTMLAnchorElement | null>>({});

  console.log('USER', user?.role);
  
  const menuItems: NavItem[] = [
    { path: '/', label: 'Dashboard', icon: Home },
    { path: '/users', label: 'Users', icon: User },
    { path: '/support', label: 'Support', icon:  HeadphonesIcon },
    { path: '/profile', label: 'Profile', icon: User },
    { path: '/email-template-management', label: 'Email Template Management', icon: Mail },
    ...(user?.role !== 'support' ? [{ path: '/settings', label: 'Settings', icon: Settings }] : []),
    { path: '/contact', label: 'Contact', icon: Phone },
  ];

  const utilityItems: NavItem[] = [
    // { path: '/help', label: 'Help', icon: HelpCircle, permission: PERMISSIONS.GENERIC_PERMISSION },
    { path: '#', label: 'Logout', icon: LogOut, onClick: doLogout },
  ];

  const isActive = useCallback(
    (path: string) => {
      if (path === '/') {
        return location.pathname === '/';
      }
      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  const renderNavItems = useCallback(
    (items: NavItem[]) =>
      items.map((item) => {
        if (item.permission && !checkPermission(item.permission)) {
          return null;
        }

        // Assign ref based on item.path
        const setRef = (el: HTMLAnchorElement | null) => {
          itemRefs.current[item.path] = el;
        };

        return (
          <div key={item.path} className="relative group">
            <Link
              ref={setRef}
              to={item.path}
              className={`flex items-center py-3 px-2 text-sm rounded-lg transition duration-200 ${isActive(item.path)
                ? 'bg-primary-100 text-primary-950'
                : ' hover:bg-primary-100'
                }`}
              onClick={(e) => {
                if (item.onClick) {
                  e.preventDefault();
                  item.onClick();
                } else {
                  setCurrentPage(item.label);
                }
              }}
            >
              <item.icon className={`h-4 w-4 ${collapsed ? 'mx-auto' : 'mr-2'}`} />
              {!collapsed && <span className="truncate text-sm font-medium">{item.label}</span>}
            </Link>
            {collapsed && itemRefs.current[item.path] && (
              <Tooltip text={item.label} targetRef={{ current: itemRefs.current[item.path] }} />
            )}
          </div>
        );
      }),
    [checkPermission, collapsed, setCurrentPage, isActive]
  );

  return (
    <>
      <aside
        className={`fixed top-0 left-0 h-full bg-white ${
          collapsed ? 'w-16' : 'w-64'
        } transition-all duration-200 z-20`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center px-4">
            <div className={collapsed ? 'w-full flex justify-center' : 'w-full flex justify-start'}>
              <Link to="/">
                {collapsed ? (
                  <img
                    src="https://d3402d7u0raa48.cloudfront.net/UJTW58TCB/Image/de356639-c0d2-4dea-9602-61e96178dc04.jpg"
                    alt="Hedra Icon"
                    className="h-8 w-8 object-contain"
                  />
                ) : (
                  <img
                    src="https://d3402d7u0raa48.cloudfront.net/UJTW58TCB/Image/27b28d81-ebfc-4732-a4ee-eaa93fc5e315.jpg"
                    alt="Hedra Logo"
                    className="h-10 object-contain"
                  />
                )}
              </Link>
            </div>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 overflow-x-hidden overflow-y-auto [&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar]:hover:w-1.5 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:rounded-full ${collapsed ? 'p-2' : 'p-4'}`}>
              {/* Main Menu with updated spacing */}
              <div className="space-y-2">
                {renderNavItems(menuItems)}
              </div>
          </nav>
          
          {/* Update utility items section */}
          <div className="mt-auto border-t border-slate-100">
            <div className={`space-y-0.5 ${collapsed ? 'p-2' : 'p-4'}`}>
              {renderNavItems(utilityItems)}
            </div>
          </div>

        </div>

        {/* Update collapse button styling */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-1/2 transform -translate-y-1/2 p-1.5 bg-white border border-slate-200 rounded-full hover:bg-slate-50 transition-all duration-200 z-30"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5 text-slate-600" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5 text-slate-600" />
          )}
        </button>
      </aside>

      {/* Spacer */}
      {/* <div className={`${collapsed ? 'w-16' : 'w-64'} transition-all duration-200`} /> */}
    </>
  );
};
